import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  TextField,
  Autocomplete,
  CircularProgress,
  Link,
  Alert,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import axiosInstance from '../../../../utils/axiosInstance'
import { Job, JobLinkableCampaign } from '../../../../types/job'
import { useToast } from '../../../../contexts/ToastContext'
import { useSelector } from '../../../../store/store'
import { USER_ROLES, isEndCustomer } from '../../../../constants'
import CreateOrder from '../../../Orders/CreateOrder'
import { formatJobLocation } from '../../../../utils/jobSearch'

interface Order {
  id: string
  jobName: string
  displayId: number
  customer: {
    name: string
  } | null
}

interface JobDetailsModalProps {
  job: Job | null
  open: boolean
  onClose: () => void
  onSaved?: () => void
}

const JobDetailsModal = ({ job, open, onClose, onSaved }: JobDetailsModalProps) => {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  // Campaigns from the job owner's own connected Meta account. Picking one links the job
  // through it; the server creates the order behind it when the campaign has none.
  const [campaigns, setCampaigns] = useState<JobLinkableCampaign[]>([])
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<JobLinkableCampaign | null>(null)
  const [saving, setSaving] = useState(false)
  const { showToast } = useToast()
  const [createOrderOpen, setCreateOrderOpen] = useState(false)
  const { role } = useSelector((state) => state.user.userInfo)
  // End customers only deal with their own customer, so its name adds nothing.
  const showCustomer = !isEndCustomer(role)
  // Admin-only for now; customers may get it later.
  const canCreateOrderFromJob = role === USER_ROLES.ADMIN.value

  const formatOrder = (order: Pick<Order, 'displayId' | 'jobName'> & Partial<Order>) =>
    showCustomer && order.customer !== undefined
      ? `#${order.displayId} - ${order.jobName} (${order.customer?.name || 'N/A'})`
      : `#${order.displayId} - ${order.jobName}`

  const handleOrderCreated = async (order: { id?: string } | undefined) => {
    if (!job || !order?.id) return
    try {
      await axiosInstance.put(`/jobs/${job.id}`, { orderId: order.id })
      showToast('Order created and linked to this job', 'success')
    } catch (error) {
      console.error('Error linking new order to job:', error)
      showToast('Order created, but it could not be linked to this job', 'warning')
    }
    onSaved?.()
  }

  useEffect(() => {
    if (open && job) {
      setLoading(true)
      // Only the job's own customer's orders can be linked (enforced by PUT /jobs/:id).
      axiosInstance
        .get(`/jobs/${job.id}/linkable-orders`)
        .then((response) => {
          const ordersList = response.data.orders || []
          setOrders(ordersList)

          // If job has an orderId, find and select that order
          if (job.orderId) {
            const order = ordersList.find((o: Order) => o.id === job.orderId)
            setSelectedOrder(order || null)
          } else {
            setSelectedOrder(null)
          }
        })
        .catch((error) => {
          console.error('Error fetching orders:', error)
          showToast('Failed to load orders', 'error')
        })
        .finally(() => {
          setLoading(false)
        })

      setCampaignsLoading(true)
      axiosInstance
        .get(`/jobs/${job.id}/linkable-campaigns`)
        .then((response) => {
          const campaignsList: JobLinkableCampaign[] = response.data.campaigns || []
          setCampaigns(campaignsList)
          setSelectedCampaign(campaignsList.find((c) => c.id === job.metaCampaignId) || null)
        })
        .catch((error) => {
          console.error('Error fetching campaigns:', error)
          setCampaigns([])
        })
        .finally(() => {
          setCampaignsLoading(false)
        })
    }
  }, [open, job, showToast])

  // One campaign serves one job: a campaign whose order already has another job is not offered.
  const linkedToOtherJob = (campaign: JobLinkableCampaign) =>
    campaign.task?.jobs.find((linked) => linked.id !== job?.id)

  const describeCampaign = (campaign: JobLinkableCampaign) => {
    const otherJob = linkedToOtherJob(campaign)
    if (otherJob) return `Linked to job "${otherJob.title}"`
    if (campaign.task && campaign.task.id !== job?.orderId) {
      return `Linked to order #${campaign.task.displayId}`
    }
    return `${campaign.page ? `${campaign.page} · ` : ''}${campaign.totalLeadsCount} leads`
  }

  const handleSave = async () => {
    if (!job) return

    setSaving(true)
    try {
      if (selectedCampaign) {
        const { data } = await axiosInstance.put(`/jobs/${job.id}`, {
          metaCampaignId: selectedCampaign.id,
        })
        showToast(
          data?.createdOrder
            ? `Job linked to the campaign; order #${data.order?.displayId} was created for it`
            : 'Job linked to the campaign',
          'success'
        )
      } else {
        // Update the job with the selected order
        await axiosInstance.put(`/jobs/${job.id}`, {
          orderId: selectedOrder?.id || null,
        })
        showToast('Job updated successfully', 'success')
      }

      onSaved?.()
      onClose()
    } catch (error: any) {
      console.error('Error updating job:', error)
      showToast(error?.response?.data?.error || 'Failed to update job', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!job) return null

  // Falls back to the order reported with the job when it is not among the linkable
  // orders (e.g. a link made before links were restricted to the job's customer).
  const connectedOrder = selectedOrder || job.order || null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">{job.title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            External ID
          </Typography>
          <Typography variant="body1" paragraph>
            {job.externalId || job.referenceNumber}
          </Typography>

          {showCustomer && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Customer
              </Typography>
              <Typography variant="body1" paragraph>
                {job.customer?.name || '-'}
              </Typography>
            </>
          )}

          <Typography variant="subtitle1" gutterBottom>
            Location
          </Typography>
          <Typography variant="body1" paragraph>
            {job.location}
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            Description
          </Typography>
          <Typography variant="body1" paragraph>
            {job.description || '-'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>
            Connected Order
          </Typography>
          {job.orderId && connectedOrder ? (
            <Box sx={{ mb: 2 }}>
              <Link
                component={RouterLink}
                to={`/orders/${connectedOrder.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <Typography variant="body1" color="primary">
                  {formatOrder(connectedOrder)}
                </Typography>
              </Link>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No order connected
            </Typography>
          )}
          {job.metaCampaign && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2 }}>
              via Meta campaign: {job.metaCampaign.name}
            </Typography>
          )}

          {campaigns.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Connect to Meta campaign
              </Typography>
              <Autocomplete
                id="campaign-select"
                options={campaigns}
                getOptionLabel={(campaign) => campaign.name}
                getOptionDisabled={(campaign) => !!linkedToOtherJob(campaign)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedCampaign}
                onChange={(_, newValue) => {
                  // Clearing the campaign also clears the order that came with it.
                  if (!newValue && selectedOrder?.id === selectedCampaign?.task?.id) {
                    setSelectedOrder(null)
                  }
                  setSelectedCampaign(newValue)
                  if (newValue) setSelectedOrder(null)
                }}
                renderOption={(props, campaign) => (
                  <li {...props} key={campaign.id}>
                    <Box>
                      <Typography variant="body1">{campaign.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {describeCampaign(campaign)}
                      </Typography>
                    </Box>
                  </li>
                )}
                loading={campaignsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select campaign from your Meta account"
                    variant="outlined"
                    fullWidth
                  />
                )}
                sx={{ mb: 1 }}
              />
              {selectedCampaign?.task && selectedCampaign.task.id !== job.orderId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This campaign is already linked to order #{selectedCampaign.task.displayId} –{' '}
                  {selectedCampaign.task.jobName}. Saving connects this job to that order.
                </Alert>
              )}
              {selectedCampaign && !selectedCampaign.task && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  An order will be created automatically for this campaign and its leads.
                </Alert>
              )}
              {!selectedCampaign && <Box sx={{ mb: 2 }} />}
            </>
          )}

          <Typography variant="subtitle1" gutterBottom>
            Connect to Order
          </Typography>
          <Autocomplete
            id="order-select"
            options={orders}
            getOptionLabel={formatOrder}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedOrder}
            onChange={(_, newValue) => {
              setSelectedOrder(newValue)
              if (newValue) setSelectedCampaign(null)
            }}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Order"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Created at
              </Typography>
              <Typography variant="body2">
                {job.externalCreatedAt ? new Date(job.externalCreatedAt).toLocaleDateString() : '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Imported at
              </Typography>
              <Typography variant="body2">
                {job.importedAt ? new Date(job.importedAt).toLocaleDateString() : '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {/* Only for unlinked jobs, so creating an order never silently moves an existing link. */}
        {canCreateOrderFromJob && !job.orderId && (
          <Button onClick={() => setCreateOrderOpen(true)} sx={{ mr: 'auto' }}>
            Create order from this job
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
      {canCreateOrderFromJob && createOrderOpen && (
        <Dialog fullScreen open onClose={() => setCreateOrderOpen(false)}>
          <CreateOrder
            handleClose={() => setCreateOrderOpen(false)}
            prefill={{
              customer: job.customer?.id,
              jobName: job.title,
              place: formatJobLocation(job.location),
              adDescription: job.description,
            }}
            onCreated={handleOrderCreated}
          />
        </Dialog>
      )}
    </Dialog>
  )
}

export default JobDetailsModal
